/**
 * FI Data Download Utilities
 *
 * Shared utilities for downloading FI statement data in XML format.
 * Consolidates download logic used by ConsentRequestPage and FiDataDrawer.
 */

import { extractCombinedXml, extractDataArray } from './xml-utils';

export interface XmlDownloadItem {
  content: string;
  filename: string;
}

export interface XmlDownloadResult {
  downloads: XmlDownloadItem[];
  skippedCount: number;
  warning: string | null;
}

/**
 * Safely extracts account suffix for filename.
 * Handles short/missing account numbers gracefully.
 *
 * @param maskedAccNumber - The masked account number
 * @returns Last 5 characters, or full string if shorter, or 'unknown' if empty
 */
export const getAccountSuffix = (maskedAccNumber: string | null | undefined): string => {
  const accNumber = maskedAccNumber ?? '';
  if (accNumber.length === 0) {
    return 'unknown';
  }
  // Return last 5 characters, or full string if shorter than 5
  return accNumber.length >= 5 ? accNumber.slice(-5) : accNumber;
};

/**
 * Builds XML download items from API response.
 * Supports both account-wise and combined download modes.
 * Returns warnings for missing data or skipped accounts.
 *
 * @param response - The API response from downloadFiDataXml
 * @param downloadAccountWiseXml - Whether to download separate files per account
 * @param accountId - The account ID for combined download filename
 * @returns Result object with downloads, skipped count, and warning message
 */
export const buildXmlDownloads = (
  response: unknown,
  downloadAccountWiseXml: boolean,
  accountId: string
): XmlDownloadResult => {
  const dataArray = extractDataArray(response);

  // Issue #3: Check for empty/missing data array
  if (!dataArray || dataArray.length === 0) {
    console.warn('[buildXmlDownloads] No data array found in response, returning empty XML wrapper');
    const combinedXml = extractCombinedXml(response);
    return {
      downloads: [
        {
          content: combinedXml,
          filename: `AA FI Statement For ${accountId}.xml`,
        },
      ],
      skippedCount: 0,
      warning: 'No account data found in response. Downloaded file may be empty.',
    };
  }

  if (downloadAccountWiseXml) {
    // Issue #4: Track skipped accounts with missing xmlData
    const validItems = dataArray.filter((item) => typeof item?.xmlData === 'string');
    const skippedCount = dataArray.length - validItems.length;

    if (skippedCount > 0) {
      console.warn(
        `[buildXmlDownloads] ${skippedCount} account(s) skipped due to missing XML data`
      );
    }

    const downloads = validItems.map((item) => {
      const accountSuffix = getAccountSuffix(item.maskedAccNumber);
      return {
        content: item.xmlData as string,
        filename: `AA FI Statement For ${accountSuffix}.xml`,
      };
    });

    return {
      downloads,
      skippedCount,
      warning:
        skippedCount > 0
          ? `${skippedCount} account(s) had no XML data and were skipped.`
          : null,
    };
  }

  // Combined download: Remove XML declarations, join, wrap in <Accounts>
  const combinedXml = extractCombinedXml(response);
  return {
    downloads: [
      {
        content: combinedXml,
        filename: `AA FI Statement For ${accountId}.xml`,
      },
    ],
    skippedCount: 0,
    warning: null,
  };
};
